import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@app/../environments/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  AdminContactDepartment,
  AdminContactInquiry,
  AdminContactPerson,
  ContactAdminActionResponse,
  ContactAdminCollection,
  ContactAdminResponse,
  ContactDepartmentRequestDTO,
  ContactInquiryStatusUpdateRequest,
  ContactPersonRequestDTO,
  ContactSieveParams
} from '@app/model/contact.model';

/**
 * ContactManagementDataService
 * ---------------------------
 * Wrapper for the **Admin CMS APIs** of the Contact module
 * (see contact-api-integration-guide.md, section 5).
 *
 * Endpoint base:
 *  - /api/contactDepartments
 *  - /api/contactPersons
 *  - /api/contactInquiries
 *
 * Requires the Authorization Bearer Token header (auto-added by the Interceptor).
 */
@Injectable({
  providedIn: 'root'
})
export class ContactManagementDataService {
  private readonly departmentsUrl = environment.backendhost + '/api/contactDepartments';
  private readonly personsUrl = environment.backendhost + '/api/contactPersons';
  private readonly inquiriesUrl = environment.backendhost + '/api/contactInquiries';

  constructor(private http: HttpClient) {}

  // ====================== 5.1. Departments / Tabs ======================

  /** A. List departments (Sieve) - returns a flat array (used for dropdown/form). */
  getDepartments(params: ContactSieveParams = {}): Observable<AdminContactDepartment[]> {
    return this.http
      .get<ContactAdminResponse<ContactAdminCollection<AdminContactDepartment> | AdminContactDepartment[]>>(
        this.departmentsUrl,
        { params: this.buildSieveParams(params) }
      )
      .pipe(map((res) => this.extractCollection(res)));
  }

  /** A.2. List departments (Sieve) - returns { collection, total } for server-side pagination. */
  getDepartmentsPaged(params: ContactSieveParams = {}): Observable<ContactAdminCollection<AdminContactDepartment>> {
    return this.http
      .get<ContactAdminResponse<ContactAdminCollection<AdminContactDepartment> | AdminContactDepartment[]>>(
        this.departmentsUrl,
        { params: this.buildSieveParams(params) }
      )
      .pipe(map((res) => this.extractCollectionWithTotal(res, params)));
  }

  /** B. Department detail. */
  getDepartment(id: string): Observable<AdminContactDepartment | null> {
    return this.http
      .get<ContactAdminResponse<AdminContactDepartment>>(this.departmentsUrl + '/' + encodeURIComponent(id))
      .pipe(map((res) => (res && res.data ? res.data : null)));
  }

  /** C. Create a new department (Flat Request Body). */
  createDepartment(payload: ContactDepartmentRequestDTO): Observable<ContactAdminActionResponse> {
    return this.http.post<ContactAdminActionResponse>(this.departmentsUrl, payload);
  }

  /** D. Update a department (Flat Request Body). */
  updateDepartment(id: string, payload: ContactDepartmentRequestDTO): Observable<ContactAdminActionResponse> {
    return this.http.put<ContactAdminActionResponse>(
      this.departmentsUrl + '/' + encodeURIComponent(id),
      payload
    );
  }

  /** E. Soft-delete a department. */
  deleteDepartment(id: string): Observable<ContactAdminActionResponse> {
    return this.http.delete<ContactAdminActionResponse>(this.departmentsUrl + '/' + encodeURIComponent(id));
  }

  /** F. Upload a department image. */
  uploadDepartmentImage(id: string, imageFile: File): Observable<ContactAdminActionResponse> {
    const formData = new FormData();
    formData.append('file', imageFile);
    return this.http.put<ContactAdminActionResponse>(
      this.departmentsUrl + '/' + encodeURIComponent(id) + '/image',
      formData
    );
  }

  // ====================== 5.2. Persons ======================

  /** A. List persons (Sieve) - returns a flat array (used for dropdown/form). */
  getPersons(params: ContactSieveParams = {}): Observable<AdminContactPerson[]> {
    return this.http
      .get<ContactAdminResponse<ContactAdminCollection<AdminContactPerson> | AdminContactPerson[]>>(
        this.personsUrl,
        { params: this.buildSieveParams(params) }
      )
      .pipe(map((res) => this.extractCollection(res)));
  }

  /** A.2. List persons (Sieve) - returns { collection, total } for server-side pagination. */
  getPersonsPaged(params: ContactSieveParams = {}): Observable<ContactAdminCollection<AdminContactPerson>> {
    return this.http
      .get<ContactAdminResponse<ContactAdminCollection<AdminContactPerson> | AdminContactPerson[]>>(
        this.personsUrl,
        { params: this.buildSieveParams(params) }
      )
      .pipe(map((res) => this.extractCollectionWithTotal(res, params)));
  }

  /** B. Person detail. */
  getPerson(id: string): Observable<AdminContactPerson | null> {
    return this.http
      .get<ContactAdminResponse<AdminContactPerson>>(this.personsUrl + '/' + encodeURIComponent(id))
      .pipe(map((res) => (res && res.data ? res.data : null)));
  }

  /** C. Create a new person (Flat Request Body). */
  createPerson(payload: ContactPersonRequestDTO): Observable<ContactAdminActionResponse> {
    return this.http.post<ContactAdminActionResponse>(this.personsUrl, payload);
  }

  /** D. Update a person (Flat Request Body). */
  updatePerson(id: string, payload: ContactPersonRequestDTO): Observable<ContactAdminActionResponse> {
    return this.http.put<ContactAdminActionResponse>(
      this.personsUrl + '/' + encodeURIComponent(id),
      payload
    );
  }

  /** E. Soft-delete a person. */
  deletePerson(id: string): Observable<ContactAdminActionResponse> {
    return this.http.delete<ContactAdminActionResponse>(this.personsUrl + '/' + encodeURIComponent(id));
  }

  /** F. Upload a person avatar. */
  uploadPersonImage(id: string, imageFile: File): Observable<ContactAdminActionResponse> {
    const formData = new FormData();
    formData.append('file', imageFile);
    return this.http.put<ContactAdminActionResponse>(
      this.personsUrl + '/' + encodeURIComponent(id) + '/image',
      formData
    );
  }

  // ====================== 5.3. Contact inquiries ======================

  /** A. List inquiries (Sieve) - returns a flat array. */
  getInquiries(params: ContactSieveParams = {}): Observable<AdminContactInquiry[]> {
    return this.http
      .get<ContactAdminResponse<ContactAdminCollection<AdminContactInquiry> | AdminContactInquiry[]>>(
        this.inquiriesUrl,
        { params: this.buildSieveParams(params) }
      )
      .pipe(map((res) => this.extractCollection(res)));
  }

  /** A.2. List inquiries (Sieve) - returns { collection, total } for server-side pagination. */
  getInquiriesPaged(params: ContactSieveParams = {}): Observable<ContactAdminCollection<AdminContactInquiry>> {
    return this.http
      .get<ContactAdminResponse<ContactAdminCollection<AdminContactInquiry> | AdminContactInquiry[]>>(
        this.inquiriesUrl,
        { params: this.buildSieveParams(params) }
      )
      .pipe(map((res) => this.extractCollectionWithTotal(res, params)));
  }

  /** B. Inquiry detail. */
  getInquiry(id: string): Observable<AdminContactInquiry | null> {
    return this.http
      .get<ContactAdminResponse<AdminContactInquiry>>(this.inquiriesUrl + '/' + encodeURIComponent(id))
      .pipe(map((res) => (res && res.data ? res.data : null)));
  }

  /** C. Update processing status & admin notes. */
  updateInquiryStatus(
    id: string,
    payload: ContactInquiryStatusUpdateRequest
  ): Observable<ContactAdminActionResponse> {
    return this.http.put<ContactAdminActionResponse>(
      this.inquiriesUrl + '/' + encodeURIComponent(id) + '/status',
      payload
    );
  }

  /** D. Delete an inquiry. */
  deleteInquiry(id: string): Observable<ContactAdminActionResponse> {
    return this.http.delete<ContactAdminActionResponse>(this.inquiriesUrl + '/' + encodeURIComponent(id));
  }

  // ====================== Helpers ======================

  private buildSieveParams(params: ContactSieveParams): HttpParams {
    let query = new HttpParams();
    if (params.filters !== undefined && params.filters !== null && params.filters !== '') {
      query = query.set('filters', params.filters);
    }
    if (params.sorts !== undefined && params.sorts !== null && params.sorts !== '') {
      query = query.set('sorts', params.sorts);
    }
    if (params.page !== undefined && params.page !== null) {
      query = query.set('page', params.page.toString());
    }
    if (params.pageSize !== undefined && params.pageSize !== null) {
      query = query.set('pageSize', params.pageSize.toString());
    }
    return query;
  }

  /** Backend may return a paginated object {collection,total} or a flat array. */
  private extractCollection<T>(res: ContactAdminResponse<ContactAdminCollection<T> | T[]>): T[] {
    if (!res || !res.data) {
      return [];
    }
    const data: any = res.data;
    if (Array.isArray(data)) {
      return data as T[];
    }
    return Array.isArray(data.collection) ? (data.collection as T[]) : [];
  }

  /**
   * Returns { collection, total } for server-side pagination.
   * - If the backend returns a paginated object: take `total` from the response.
   * - If the backend returns a flat array (no pagination): total = array.length.
   */
  private extractCollectionWithTotal<T>(
    res: ContactAdminResponse<ContactAdminCollection<T> | T[]>,
    params: ContactSieveParams
  ): ContactAdminCollection<T> {
    if (!res || !res.data) {
      return { collection: [], total: 0, pagesize: params.pageSize || 20, pageIndex: params.page || 1 };
    }
    const data: any = res.data;
    if (Array.isArray(data)) {
      return {
        collection: data as T[],
        total: data.length,
        pagesize: params.pageSize || 20,
        pageIndex: params.page || 1
      };
    }
    return {
      collection: Array.isArray(data.collection) ? (data.collection as T[]) : [],
      total: typeof data.total === 'number' ? data.total : (data.collection || []).length,
      pagesize: typeof data.pagesize === 'number' ? data.pagesize : params.pageSize || 20,
      pageIndex: typeof data.pageIndex === 'number' ? data.pageIndex : params.page || 1
    };
  }
}
